import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Sparkles, TrendingUp, TrendingDown, AlertTriangle, Target, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  losing_momentum: { Icon: TrendingDown, color: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
  fast_growing: { Icon: TrendingUp, color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
  concentration_risk: { Icon: AlertTriangle, color: "text-red-400 bg-red-500/10 border-red-500/30" },
  high_returns: { Icon: AlertTriangle, color: "text-red-400 bg-red-500/10 border-red-500/30" },
  high_cancellations: { Icon: AlertTriangle, color: "text-red-400 bg-red-500/10 border-red-500/30" },
  growing_category: { Icon: Target, color: "text-orange-400 bg-orange-500/10 border-orange-500/30" },
  needs_attention: { Icon: AlertTriangle, color: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
};

const PRIORITY_BADGE = {
  high: "text-red-300 bg-red-500/10 border-red-500/30",
  medium: "text-amber-300 bg-amber-500/10 border-amber-500/30",
  low: "text-zinc-300 bg-zinc-700/30 border-zinc-700",
};

export default function Opportunities() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [explaining, setExplaining] = useState({});
  const [explanations, setExplanations] = useState({});

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/opportunities");
      setItems(data);
    } catch (e) {
      toast.error("Failed to load opportunities");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const explain = async (opportunity_id) => {
    setExplaining((e) => ({ ...e, [opportunity_id]: true }));
    try {
      const { data } = await api.post(`/opportunities/${opportunity_id}/explain`);
      setExplanations((m) => ({ ...m, [opportunity_id]: data.ai_explanation }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setExplaining((e) => ({ ...e, [opportunity_id]: false })); }
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="opportunities-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500/80 mb-2">Opportunities</p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>
            Where your next revenue lives
          </h1>
          <p className="mt-2 text-zinc-400 max-w-2xl">
            Generated by Milkath's Opportunity Engine — products to investigate, risks to mitigate, and growth lanes to lean into.
          </p>
        </div>
        <button
          onClick={refresh}
          className="inline-flex items-center gap-1.5 bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-zinc-200 px-3 py-2 rounded-lg text-sm transition-colors"
          data-testid="opportunities-refresh-btn"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Re-analyze
        </button>
      </div>

      <div className="mt-10 space-y-4">
        {loading ? (
          <div className="text-center py-12 text-sm text-zinc-500">Analyzing your business…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-16 text-center" data-testid="opportunities-empty">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-orange-600/10 border border-orange-600/20 flex items-center justify-center mb-4">
              <Sparkles className="h-6 w-6 text-orange-500" />
            </div>
            <h3 className="text-lg font-medium text-zinc-100" style={{ fontFamily: "var(--font-heading)" }}>No opportunities surfaced yet</h3>
            <p className="mt-2 text-sm text-zinc-400 max-w-md mx-auto">
              Milkath needs at least 30 days of order history to surface meaningful opportunities. Import another period to unlock more.
            </p>
          </div>
        ) : items.map((o) => {
          const meta = KIND_META[o.kind] || KIND_META.needs_attention;
          const Icon = meta.Icon;
          return (
            <div key={o.opportunity_id} className="rounded-2xl border border-zinc-800 bg-zinc-900/40 hover:border-zinc-700 transition-colors p-6" data-testid={`opportunity-${o.opportunity_id}`}>
              <div className="flex items-start gap-4">
                <div className={`h-10 w-10 rounded-xl border flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                  <Icon className="h-5 w-5" strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className={`text-[10px] uppercase tracking-[0.16em] font-bold px-1.5 py-0.5 rounded border ${PRIORITY_BADGE[o.priority]}`}>
                      {o.priority}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-zinc-500">{o.confidence}% confidence</span>
                    <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-600">·</span>
                    <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{o.kind.replace(/_/g, " ")}</span>
                  </div>
                  <h3 className="text-lg font-medium text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>{o.title}</h3>
                  <p className="mt-1.5 text-sm text-zinc-400">{o.description}</p>

                  <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-bold">Why</dt>
                      <dd className="mt-1 text-zinc-300">{o.reason}</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-bold">Business impact</dt>
                      <dd className="mt-1 text-zinc-300">{o.business_impact}</dd>
                    </div>
                  </dl>

                  <div className="mt-4 rounded-lg bg-zinc-950/50 border border-zinc-800 px-4 py-3">
                    <p className="text-[10px] uppercase tracking-[0.16em] text-orange-500/80 font-bold mb-1">Recommended next action</p>
                    <p className="text-sm text-zinc-200">{o.recommended_action}</p>
                  </div>

                  {explanations[o.opportunity_id] && (
                    <div className="mt-4 rounded-lg bg-orange-500/5 border border-orange-500/20 px-4 py-3" data-testid={`opportunity-ai-explanation-${o.opportunity_id}`}>
                      <p className="text-[10px] uppercase tracking-[0.16em] text-orange-500 font-bold mb-1 inline-flex items-center gap-1">
                        <Sparkles className="h-3 w-3" /> Milkath AI
                      </p>
                      <p className="text-sm text-zinc-200 leading-relaxed">{explanations[o.opportunity_id]}</p>
                    </div>
                  )}

                  {!explanations[o.opportunity_id] && (
                    <button
                      onClick={() => explain(o.opportunity_id)}
                      disabled={explaining[o.opportunity_id]}
                      className="mt-4 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-orange-500/10 hover:bg-orange-500/20 text-orange-300 border border-orange-500/30 transition-colors disabled:opacity-50"
                      data-testid={`opportunity-explain-btn-${o.opportunity_id}`}
                    >
                      {explaining[o.opportunity_id] ? (<><Loader2 className="h-3 w-3 animate-spin" /> Asking Milkath AI…</>) :
                       (<><Sparkles className="h-3 w-3" /> Explain with AI</>)}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
